/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  RiskSpanRedacted,
  RiskSpanRedacted$inboundSchema,
} from "./riskspanredacted.js";

/**
 * Recorded enforcement outcome, independent of policy configuration.
 */
export const RiskResultRedactedEnforcementOutcome = {
  Logged: "logged",
  Denied: "denied",
  Withheld: "withheld",
  WarnedPending: "warned_pending",
  WarnedAcknowledged: "warned_acknowledged",
  WarnedAbandoned: "warned_abandoned",
  Quarantined: "quarantined",
} as const;
/**
 * Recorded enforcement outcome, independent of policy configuration.
 */
export type RiskResultRedactedEnforcementOutcome = OpenEnum<
  typeof RiskResultRedactedEnforcementOutcome
>;

export type RiskResultRedacted = {
  /**
   * The chat content part that was scanned, when the finding is anchored to a content part.
   */
  chatContentPartId?: string | undefined;
  /**
   * The chat session containing the message.
   */
  chatId?: string | undefined;
  /**
   * The chat message that was scanned, when the finding is anchored to a message.
   */
  chatMessageId?: string | undefined;
  /**
   * Title of the chat session.
   */
  chatTitle?: string | undefined;
  /**
   * Confidence score for this finding.
   */
  confidence?: number | undefined;
  /**
   * When this result was created.
   */
  createdAt: Date;
  /**
   * Human-readable description of the finding.
   */
  description?: string | undefined;
  /**
   * Recorded enforcement outcome, independent of policy configuration.
   */
  enforcementOutcome?: RiskResultRedactedEnforcementOutcome | undefined;
  /**
   * The mediated MCP execution (tool call, resource read or prompt get) that raised this finding. Filter listResults by it to list that execution's non-dismissed findings.
   */
  executionId?: string | undefined;
  /**
   * The result ID.
   */
  id: string;
  /**
   * Whether MCP identity stamped validated principal provenance.
   */
  identityStamped?: boolean | undefined;
  /**
   * Opaque fingerprint of the original match in the form `<redacted len=N sha=XXXXXXXX>` where N is the byte length of the original match and XXXXXXXX is the first 8 hex characters of sha256(match). For shadow_mcp findings the original match value (a non-sensitive server URL or command identifier) is passed through verbatim.
   */
  matchRedacted: string;
  /**
   * MCP method or equivalent mediated operation.
   */
  mcpMethod?: string | undefined;
  /**
   * Concrete MCP server that executed the operation.
   */
  mcpServerId?: string | undefined;
  /**
   * Concrete mediation surface where the execution was observed.
   */
  mediationSurface?: string | undefined;
  /**
   * Outer gateway that routed the execution, when present.
   */
  metaMcpServerId?: string | undefined;
  /**
   * Which phase of the mediated MCP execution was scanned: its request or its response.
   */
  phase?: string | undefined;
  /**
   * The risk policy ID.
   */
  policyId: string;
  /**
   * Policy version when this result was produced.
   */
  policyVersion: number;
  /**
   * Whether the original finding carried byte-position information within the source message. Exact positions are intentionally not exposed to avoid reconstruction attacks.
   */
  positionKnown: boolean;
  /**
   * Credential provenance class resolved by MCP identity.
   */
  principalKind?: string | undefined;
  /**
   * The matched rule identifier.
   */
  ruleId?: string | undefined;
  /**
   * Detection source (e.g. gitleaks, presidio, shadow_mcp).
   */
  source: string;
  /**
   * All matched spans attributed to this finding, each with its match replaced by an opaque fingerprint.
   */
  spansRedacted?: Array<RiskSpanRedacted> | undefined;
  /**
   * Tags from the detection rule.
   */
  tags?: Array<string> | undefined;
  /**
   * Name of the concrete tool, when applicable.
   */
  toolName?: string | undefined;
  /**
   * Toolset serving the execution, when present.
   */
  toolsetId?: string | undefined;
  /**
   * The user who owns the chat session.
   */
  userId?: string | undefined;
};

/** @internal */
export const RiskResultRedactedEnforcementOutcome$inboundSchema: z.ZodMiniType<
  RiskResultRedactedEnforcementOutcome,
  unknown
> = openEnums.inboundSchema(RiskResultRedactedEnforcementOutcome);

/** @internal */
export const RiskResultRedacted$inboundSchema: z.ZodMiniType<
  RiskResultRedacted,
  unknown
> = z.pipe(
  z.object({
    chat_content_part_id: z.optional(z.string()),
    chat_id: z.optional(z.string()),
    chat_message_id: z.optional(z.string()),
    chat_title: z.optional(z.string()),
    confidence: z.optional(z.number()),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    description: z.optional(z.string()),
    enforcement_outcome: z.optional(
      RiskResultRedactedEnforcementOutcome$inboundSchema,
    ),
    execution_id: z.optional(z.string()),
    id: z.string(),
    identity_stamped: z.optional(z.boolean()),
    match_redacted: z.string(),
    mcp_method: z.optional(z.string()),
    mcp_server_id: z.optional(z.string()),
    mediation_surface: z.optional(z.string()),
    meta_mcp_server_id: z.optional(z.string()),
    phase: z.optional(z.string()),
    policy_id: z.string(),
    policy_version: z.int(),
    position_known: z.boolean(),
    principal_kind: z.optional(z.string()),
    rule_id: z.optional(z.string()),
    source: z.string(),
    spans_redacted: z.optional(z.array(RiskSpanRedacted$inboundSchema)),
    tags: z.optional(z.array(z.string())),
    tool_name: z.optional(z.string()),
    toolset_id: z.optional(z.string()),
    user_id: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "chat_content_part_id": "chatContentPartId",
      "chat_id": "chatId",
      "chat_message_id": "chatMessageId",
      "chat_title": "chatTitle",
      "created_at": "createdAt",
      "enforcement_outcome": "enforcementOutcome",
      "execution_id": "executionId",
      "identity_stamped": "identityStamped",
      "match_redacted": "matchRedacted",
      "mcp_method": "mcpMethod",
      "mcp_server_id": "mcpServerId",
      "mediation_surface": "mediationSurface",
      "meta_mcp_server_id": "metaMcpServerId",
      "policy_id": "policyId",
      "policy_version": "policyVersion",
      "position_known": "positionKnown",
      "principal_kind": "principalKind",
      "rule_id": "ruleId",
      "spans_redacted": "spansRedacted",
      "tool_name": "toolName",
      "toolset_id": "toolsetId",
      "user_id": "userId",
    });
  }),
);

export function riskResultRedactedFromJSON(
  jsonString: string,
): SafeParseResult<RiskResultRedacted, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RiskResultRedacted$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RiskResultRedacted' from JSON`,
  );
}
